import {
  type AlmanacGroup,
  useAlmanacOptions,
} from "portal/components/categoryTemplates/useAlmanacOptions";
import {
  Category,
  Role,
  roleFromJSON,
  roleToJSON,
} from "protos/robot_syncer/plant_profile";
import {
  categoryOwnershipFromPlantProfile,
  UnsavedPlantProfile,
} from "portal/utils/categoryCollection/plantProfile";
import {
  DEFAULT_CREATE_CATEGORY_ROLE,
  defaultMostSimilarForRole,
  PLANT_PROFILE_ROLE_OPTIONS,
  type PlantProfileRoleOption,
} from "portal/utils/categoryCollection/category";
import { FormikConfig } from "formik";
import { object, ObjectShape, Schema, string } from "yup";
import { resolveAlmanacCategory } from "portal/components/categoryTemplates/createCategory/resolveAlmanacCategory";
import { TFunction } from "i18next";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { v4 as uuid } from "uuid";

export type CreateCategoryFormValues = {
  name: string;
  role: PlantProfileRoleOption;
  sourceAlmanacId: string;
  mostSimilarTo: string;
};

export const categoryNameAndRoleSchema = (t: TFunction): ObjectShape => ({
  name: string().trim().required(t("utils.form.required")),
  role: string()
    .oneOf([...PLANT_PROFILE_ROLE_OPTIONS])
    .required(t("utils.form.required")),
});

export const createCategoryValidationSchema = (t: TFunction): Schema =>
  object({
    ...categoryNameAndRoleSchema(t),
    mostSimilarTo: string().required(t("utils.form.required")),
    sourceAlmanacId: string().required(t("utils.form.required")),
  });

type UseCreateCategoryFormResult = {
  almanacGroups: AlmanacGroup[];
  defaultAlmanacId: string;
  formConfig: FormikConfig<CreateCategoryFormValues>;
  isLoading: boolean;
};

export const useCreateCategoryForm = (
  unsavedPlantProfile: UnsavedPlantProfile,
  customerUuid: string | undefined,
  onAdd: (category: Category, role: Role) => void,
  defaultRole: Role = roleFromJSON(DEFAULT_CREATE_CATEGORY_ROLE)
): UseCreateCategoryFormResult => {
  const { t } = useTranslation();
  const { almanacGroups, defaultAlmanacId, isLoading } = useAlmanacOptions(
    customerUuid ?? unsavedPlantProfile.customerId
  );
  const defaultRoleOption = roleToJSON(defaultRole);

  const initialValues: CreateCategoryFormValues = useMemo(
    () => ({
      name: "",
      role: defaultRoleOption,
      sourceAlmanacId: defaultAlmanacId,
      mostSimilarTo: defaultMostSimilarForRole(defaultRoleOption),
    }),
    [defaultAlmanacId, defaultRoleOption]
  );

  return {
    almanacGroups,
    defaultAlmanacId,
    isLoading,
    formConfig: {
      initialValues,
      enableReinitialize: true,
      validationSchema: createCategoryValidationSchema(t),
      onSubmit: (
        { name, role, mostSimilarTo, sourceAlmanacId },
        { setFieldError }
      ) => {
        const resolved = resolveAlmanacCategory(
          almanacGroups,
          sourceAlmanacId,
          mostSimilarTo,
          t
        );
        if (!resolved.ok) {
          setFieldError(resolved.field, resolved.message);
          return;
        }

        const category: Category = {
          id: uuid(),
          name: name.trim(),
          chipIds: [],
          almanac: resolved.almanac,
          ...categoryOwnershipFromPlantProfile(unsavedPlantProfile),
        };
        onAdd(category, roleFromJSON(role));
      },
    },
  };
};
